import { Button, Flex, Text } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { ModalWarningContent } from '@shared/ui/modal';
import { useDeleteVacancyTemplate } from '@entities/vacancy';
import { useNavigate } from 'react-router';
import { showNotification } from '@shared/utils';
import { QueryKey } from '@shared/config';
import { useQueryClient } from '@tanstack/react-query';

const MODAL_ID = 'confirm-delete-vacancies-template';

type VacanciesTemplatesConfirmDeleteModalComponentProps = {
  id: number;
  name: string;
};

const VacanciesTemplatesConfirmDeleteModalComponent = ({
  id,
  name,
}: VacanciesTemplatesConfirmDeleteModalComponentProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: deleteVacancyTemplate, isPending } =
    useDeleteVacancyTemplate();

  const handleClose = () => {
    closeModal(MODAL_ID);
  };

  const handleDelete = () => {
    deleteVacancyTemplate(
      { ids: [id] },
      {
        onSuccess: () => {
          handleClose();

          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_VACANCIES_TEMPLATES],
          });

          showNotification({
            title: 'Шаблон удален',
            message: (
              <Flex>
                <Text>Шаблон&nbsp;</Text>
                <Text fw={600} c='primary.7'>
                  {name}
                </Text>
                <Text>&nbsp;успешно удален</Text>
              </Flex>
            ),
            variant: 'info',
          });

          navigate('/vacancies-templates-list');
        },
        onError: error => {
          showNotification({
            error,
            message:
              error.response?.data.errors.message ||
              'Не удалось удалить шаблон',
            variant: 'error',
          });
        },
      }
    );
  };

  return (
    <Flex>
      <Flex direction='column'>
        <ModalWarningContent
          message={
            <Text>
              Вы действительно хотите удалить шаблон&nbsp;
              <Text component='span' fw={600} c='primary.7'>
                {name}
              </Text>
              ?
            </Text>
          }
        />

        <Flex pt={12} gap={8}>
          <Button variant='outline' onClick={handleClose}>
            Отмена
          </Button>
          <Button variant='primary' onClick={handleDelete} loading={isPending}>
            Удалить
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const useVacanciesTemplatesConfirmDeleteModal = () => {
  const openConfirmDeleteModal = (id: number, name: string) =>
    openModal({
      modalId: MODAL_ID,
      title: 'Удалить',
      children: (
        <VacanciesTemplatesConfirmDeleteModalComponent id={id} name={name} />
      ),
    });

  return { openConfirmDeleteModal };
};
